/* eslint-disable react/jsx-no-target-blank */
import { Button as RoccoButton } from '@rocco/components/button';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { cn } from '@rocco/utils/cn';

const HIDE_CTA_AND_CONTENT = true;

/**
 * The profile title copies the homepage headline (BC-4481): Helvetica Neue
 * Light rather than the brand `font-sans` (Neue Haas Grotesk).
 */
const HEADLINE_FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';

const LONG_TITLE_CHARACTER_COUNT = 42;
const EXTRA_LONG_TITLE_CHARACTER_COUNT = 60;

const getHeadlineSizeClass = (title: string) => {
    const titleLength = title.trim().length;

    if (titleLength > EXTRA_LONG_TITLE_CHARACTER_COUNT) {
        return 'md:text-[clamp(4rem,4.5vw,4.75rem)] md:leading-[clamp(4.5rem,5vw,5.2rem)] md:tracking-[-0.04rem]';
    }

    if (titleLength > LONG_TITLE_CHARACTER_COUNT) {
        return 'md:text-[clamp(4.75rem,5.25vw,5.5rem)] md:leading-[clamp(5.2rem,5.75vw,5.95rem)] md:tracking-[-0.05rem]';
    }

    return 'md:text-[clamp(5.625rem,6.25vw,7.5rem)] md:leading-[clamp(6.1rem,6.77vw,8.125rem)] md:tracking-[-0.06rem]';
};

export const ProfessionalHeroBannerContent = ({
    professional,
    variant = 'default',
}: {
    professional: {
        Title: string;
        Content?: string;
        Website: string;
        ID: number;
        filteredName?: string;
        filteredSymbol?: string;
        afterSymbol?: string;
        hasSymbol: boolean;
    };
    variant?: 'default' | 'compact';
}) => {
    const {
        Title,
        Website,
        ID,
        Content,
        filteredName,
        filteredSymbol,
        afterSymbol,
        hasSymbol,
    } = professional;
    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(Website, {
            libraryItemID: ID,
            professionalID: ID,
            professionalTitle: Title,
            source: 'ProfessionalHeroBanner',
        });
    const headlineSizeClass = getHeadlineSizeClass(Title);

    return (
        <div
            className={cn(
                'pointer-events-none absolute left-0 z-[3] flex w-full flex-col px-4 md:mt-[10rem] md:w-[70rem] md:pl-12',
                variant === 'compact'
                    ? 'bottom-0'
                    : // Mobile anchors to the bottom so the title clears the big
                      // "A" logomark; desktop sits in the lower band per Figma
                      // 1478-28257 (title bottom ≈ 69% of the banner).
                      'bottom-0 md:bottom-[25%] md:w-240',
            )}
        >
            <h1
                className={cn(
                    'm-0 max-w-full pb-12 text-white md:pb-[3.2rem]',
                    variant === 'compact'
                        ? 'text-[2.25rem] leading-[116%] tracking-[-0.15rem] md:text-18 md:leading-[110%]'
                        : // Homepage headline treatment (BC-4481).
                          cn(
                              'text-[2.5rem] leading-[1.05] font-light tracking-[-0.01em] [text-wrap:balance] md:max-w-[52rem]',
                              headlineSizeClass,
                          ),
                )}
                style={
                    variant === 'compact'
                        ? undefined
                        : { fontFamily: HEADLINE_FONT }
                }
            >
                {hasSymbol ? (
                    <>
                        {filteredName}
                        <span className="mr-5 align-super text-[50%]">
                            {filteredSymbol}
                        </span>
                        {afterSymbol}
                    </>
                ) : (
                    Title
                )}
            </h1>

            {!HIDE_CTA_AND_CONTENT && Content && Content.length > 0 && (
                <div className="pr-24 pb-12 md:max-w-[35.625rem] md:pr-0 md:pb-[3.2rem]">
                    <div
                        className="line-clamp-2 h-[2.6em] text-4 leading-[130%] tracking-[0.025rem] text-white md:text-5"
                        dangerouslySetInnerHTML={{
                            __html: Content ?? '',
                        }}
                    />
                </div>
            )}

            {!HIDE_CTA_AND_CONTENT && Website && (
                <div className="flex items-center gap-9">
                    <RoccoButton
                        color="transparent"
                        inverted={true}
                        asChild
                        size={'compact'}
                        shape={'square'}
                    >
                        <a
                            href={referralTrackingURL}
                            target="_blank"
                            {...referralTrackingParams}
                            style={{
                                textDecoration: 'underline',
                                textUnderlineOffset: '0.3rem',
                                letterSpacing: '0.035rem',
                            }}
                        >
                            {'View website'}
                        </a>
                    </RoccoButton>
                </div>
            )}
        </div>
    );
};
